// Strapi API Service - Uses backend proxy to avoid CORS issues
const STRAPI_URL = 'https://sincere-memory-8e1822a09b.strapiapp.com';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Fetch all published articles from Strapi via backend proxy
 */
export async function getArticles() {
  try {
    const response = await fetch(
      `${API_URL}/api/strapi/articles`
    );
    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching articles:', error);
    return [];
  }
}

/**
 * Fetch a single article by slug via backend proxy
 */
export async function getArticleBySlug(slug) {
  try {
    const response = await fetch(
      `${API_URL}/api/strapi/articles/by-slug/${encodeURIComponent(slug)}`
    );
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error('Error fetching article:', error);
    return null;
  }
}

/**
 * Fetch all categories
 */
export async function getCategories() {
  try {
    const response = await fetch(`${API_URL}/categories?populate=*`);
    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
}

/**
 * Get the full URL for a Strapi media asset
 */
export function getStrapiMediaUrl(media) {
  if (!media) return null;
  
  // If it's already a full URL, return it
  if (media.url?.startsWith('http')) {
    return media.url;
  }
  
  // Otherwise prepend Strapi URL
  return `${STRAPI_URL}${media.url}`;
}

/**
 * Format date for display
 */
export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

/**
 * Extract headings from Strapi blocks for Table of Contents
 * Handles the component-based block structure (shared.rich-text with markdown)
 */
export function extractHeadings(blocks) {
  if (!blocks || !Array.isArray(blocks)) return [];
  
  const headings = [];
  
  blocks.forEach((block) => {
    // Handle shared.rich-text blocks with markdown body
    if (block.__component === 'shared.rich-text' && block.body) {
      // Extract ## and ### headings from markdown
      const lines = block.body.split('\n');
      lines.forEach((line) => {
        const h2Match = line.match(/^##\s+(.+)$/);
        const h3Match = line.match(/^###\s+(.+)$/);
        
        if (h2Match) {
          const text = h2Match[1].trim();
          const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          headings.push({ id, text, level: 2 });
        } else if (h3Match) {
          const text = h3Match[1].trim();
          const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          headings.push({ id, text, level: 3 });
        }
      });
    }
  });
  
  return headings;
}

/**
 * Render Strapi component blocks to HTML
 * Handles: shared.rich-text (markdown), shared.quote, shared.media, shared.slider
 */
export function renderBlocksToHtml(blocks) {
  if (!blocks || !Array.isArray(blocks)) return '';
  
  return blocks.map((block) => {
    switch (block.__component) {
      case 'shared.rich-text':
        return renderMarkdownToHtml(block.body || '');
      
      case 'shared.quote':
        return `
          <blockquote class="border-l-4 border-blue-500 pl-4 py-3 my-6 bg-gray-50 rounded-r">
            <p class="italic text-gray-700 text-lg">"${escapeHtml(block.body || '')}"</p>
            ${block.title ? `<footer class="mt-2 text-sm text-gray-500 font-medium">— ${escapeHtml(block.title)}</footer>` : ''}
          </blockquote>
        `;
      
      case 'shared.media':
        if (block.file) {
          const imgUrl = getStrapiMediaUrl(block.file);
          const alt = block.file.alternativeText || '';
          return imgUrl ? `
            <figure class="my-6">
              <img src="${imgUrl}" alt="${alt}" class="rounded-lg w-full" loading="lazy" />
              ${block.file.caption ? `<figcaption class="text-sm text-gray-500 mt-2 text-center">${escapeHtml(block.file.caption)}</figcaption>` : ''}
            </figure>
          ` : '';
        }
        return '';
      
      case 'shared.slider':
        // Handle slider with multiple files
        if (block.files && Array.isArray(block.files) && block.files.length > 0) {
          const images = block.files.map(file => {
            const imgUrl = getStrapiMediaUrl(file);
            return imgUrl ? `<img src="${imgUrl}" alt="${file.alternativeText || ''}" class="rounded-lg w-full h-64 object-cover" loading="lazy" />` : '';
          }).filter(Boolean).join('');
          return images ? `<div class="grid grid-cols-2 gap-4 my-6">${images}</div>` : '';
        }
        return '';
      
      default:
        return '';
    }
  }).join('');
}

/**
 * Convert markdown to HTML
 */
function renderMarkdownToHtml(markdown) {
  if (!markdown) return '';
  
  let html = markdown;
  
  // Convert headings (## and ###) with IDs for anchor links
  html = html.replace(/^###\s+(.+)$/gm, (match, text) => {
    const id = text.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return `<h3 id="${id}" class="text-xl font-semibold text-gray-900 mt-6 mb-3">${text.trim()}</h3>`;
  });
  
  html = html.replace(/^##\s+(.+)$/gm, (match, text) => {
    const id = text.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return `<h2 id="${id}" class="text-2xl font-bold text-gray-900 mt-8 mb-4">${text.trim()}</h2>`;
  });
  
  // Convert bold **text** and *text*
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  
  // Convert links [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
    const isExternal = url.startsWith('http');
    return `<a href="${url}" class="text-blue-600 hover:underline"${isExternal ? ' target="_blank" rel="noopener noreferrer"' : ''}>${text}</a>`;
  });
  
  // Convert unordered lists
  html = html.replace(/^- (.+)$/gm, '<li class="text-gray-700">$1</li>');
  html = html.replace(/(<li[^>]*>.*<\/li>\n?)+/g, '<ul class="list-disc list-inside mb-4 space-y-2">$&</ul>');
  
  // Convert paragraphs (lines that don't start with < are paragraphs)
  const lines = html.split('\n');
  const processedLines = [];
  let inParagraph = false;
  let paragraphContent = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Skip empty lines
    if (!line) {
      if (inParagraph && paragraphContent.length > 0) {
        processedLines.push(`<p class="mb-4 text-gray-700 leading-relaxed">${paragraphContent.join(' ')}</p>`);
        paragraphContent = [];
        inParagraph = false;
      }
      continue;
    }
    
    // If line starts with HTML tag, it's already processed
    if (line.startsWith('<')) {
      if (inParagraph && paragraphContent.length > 0) {
        processedLines.push(`<p class="mb-4 text-gray-700 leading-relaxed">${paragraphContent.join(' ')}</p>`);
        paragraphContent = [];
        inParagraph = false;
      }
      processedLines.push(line);
    } else {
      // Regular text, add to paragraph
      inParagraph = true;
      paragraphContent.push(line);
    }
  }
  
  // Don't forget remaining paragraph content
  if (paragraphContent.length > 0) {
    processedLines.push(`<p class="mb-4 text-gray-700 leading-relaxed">${paragraphContent.join(' ')}</p>`);
  }
  
  return processedLines.join('\n');
}

function escapeHtml(text) {
  if (!text) return '';
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return String(text).replace(/[&<>"']/g, m => map[m]);
}

export { STRAPI_URL };
